import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  FiGrid,
  FiShield,
  FiCreditCard,
  FiUsers,
  FiPercent,
  FiHelpCircle,
  FiSun,
  FiMoon,
  FiExternalLink,
  FiLogOut,
  FiMoreHorizontal,
  FiX,
  FiTrendingUp,
  FiAward,
  FiSettings,
  FiClock,
  FiCalendar,
  FiInbox,
  FiGift,
} from 'react-icons/fi';

import styles from './DashboardShell.module.css';
import NotificationBell from '../NotificationBell/NotificationBell';
import { useTheme } from '../../app/ThemeContext';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { logout } from '../../features/auth/authSlice';

const NAV_ITEMS = [
  {
    label: 'Overview',
    path: '/dashboard',
    Icon: FiGrid,
  },
  {
    label: 'Investment',
    path: '/dashboard/investment',
    Icon: FiTrendingUp,
  },
  {
    label: 'Portal Returns',
    path: '/dashboard/portal-investments',
    Icon: FiPercent,
  },
  {
    label: 'Partners',
    path: '/dashboard/partners',
    Icon: FiAward,
  },
  {
    label: 'Verification',
    path: '/dashboard/kyc',
    Icon: FiShield,
  },
  {
    label: 'Funds',
    path: '/dashboard/funds',
    Icon: FiCreditCard,
  },
  {
    label: 'Support',
    path: '/dashboard/support',
    Icon: FiHelpCircle,
  },
];

const MORE_ITEMS = [
  {
    label: 'BCT100 Claims',
    path: '/dashboard/bct-token-claims',
    Icon: FiGift,
  },
  {
    label: 'My Requests',
    path: '/dashboard/my-requests',
    Icon: FiInbox,
  },
  {
    label: 'Meetings',
    path: '/dashboard/meetings',
    Icon: FiCalendar,
  },
  {
    label: 'Payment History',
    path: '/dashboard/payment-history',
    Icon: FiClock,
  },
  {
    label: 'My Team',
    path: '/dashboard/team',
    Icon: FiUsers,
  },
  {
    label: 'Rebate',
    path: '/dashboard/rebate',
    Icon: FiPercent,
  },
  {
    label: 'My Rank',
    path: '/dashboard/rank',
    Icon: FiAward,
  },
];

const ALL_NAV = [...NAV_ITEMS, ...MORE_ITEMS];

interface Props {
  children: React.ReactNode;
}

const DashboardShell: React.FC<Props> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { theme, toggleTheme } = useTheme();
  const { user } = useAppSelector((state) => state.auth);
  const [moreOpen, setMoreOpen] = useState(false);

  const handleLogout = () => {
    setMoreOpen(false);
    dispatch(logout());
    navigate('/login');
  };

  const isMoreActive = MORE_ITEMS.some(
    (item) => item.path === location.pathname,
  );

  return (
    <div className={styles.root} data-theme={theme}>
      <div className={styles.mobileTopBar}>
        <Link to="/" className={styles.sidebarBrand}>
          <img
            src="/images/logo.png"
            alt="BCT"
            className={styles.sidebarLogo}
          />
          <span>BCT Platform</span>
        </Link>

        <NotificationBell />
      </div>

      <div className={styles.shell}>
        <aside className={styles.sidebar}>
          <div className={styles.sidebarTop}>
            <Link to="/" className={styles.sidebarBrand}>
              <img
                src="/images/logo.png"
                alt="BCT"
                className={styles.sidebarLogo}
              />
              <span>BCT Platform</span>
            </Link>

            <NotificationBell />
          </div>

          <div className={`${styles.navList} thinScroll`}>
            {ALL_NAV.map((item) => {
              const active = location.pathname === item.path;

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  aria-current={active ? 'page' : undefined}
                  className={`${styles.navItem} ${
                    active ? styles.navItemActive : ''
                  }`}
                >
                  <item.Icon />
                  {item.label}
                </Link>
              );
            })}
          </div>

          <div className={styles.sidebarFooter}>
            <div className={styles.iconCol}>
              <Link to="/" className={styles.iconBtn}>
                <FiExternalLink />
                <span>Website</span>
              </Link>

              <button
                type="button"
                className={styles.iconBtn}
                onClick={toggleTheme}
              >
                {theme === 'dark' ? <FiSun /> : <FiMoon />}
                <span>
                  {theme === 'dark' ? 'Light mode' : 'Dark mode'}
                </span>
              </button>

              <Link
                to="/dashboard/account"
                className={styles.iconBtn}
              >
                <FiSettings />
                <span>Account</span>
              </Link>

              <button
                type="button"
                className={styles.iconBtn}
                onClick={handleLogout}
              >
                <FiLogOut />
                <span>Logout</span>
              </button>
            </div>

            {user && (
              <div className={styles.profileCard}>
                <div className={styles.profileAvatar}>
                  {user.full_name?.[0]?.toUpperCase() || '?'}
                </div>

                <div className={styles.profileInfo}>
                  <div className={styles.profileName}>
                    {user.full_name}
                  </div>
                  <div className={styles.profileRole}>
                    {user.role}
                  </div>
                </div>
              </div>
            )}
          </div>
        </aside>

        <div className={styles.content}>{children}</div>
      </div>

      <nav className={styles.bottomNav} aria-label="Dashboard navigation">
        {NAV_ITEMS.map((item) => {
          const active = location.pathname === item.path;

          return (
            <Link
              key={item.path}
              to={item.path}
              aria-current={active ? 'page' : undefined}
              className={`${styles.bottomNavItem} ${
                active ? styles.bottomNavItemActive : ''
              }`}
            >
              <item.Icon />
              <span>{item.label}</span>
            </Link>
          );
        })}

        <button
          type="button"
          className={`${styles.bottomNavItem} ${
            isMoreActive ? styles.bottomNavItemActive : ''
          }`}
          aria-expanded={moreOpen}
          aria-controls="dashboard-more-menu"
          onClick={() => setMoreOpen(true)}
        >
          <FiMoreHorizontal />
          <span>More</span>
        </button>
      </nav>

      {moreOpen && (
        <div
          className={styles.sheetOverlay}
          onClick={() => setMoreOpen(false)}
        >
          <div
            id="dashboard-more-menu"
            className={styles.sheet}
            onClick={(event) => event.stopPropagation()}
          >
            <div className={styles.sheetHandle} />

            <div className={styles.sheetHeader}>
              <span>Menu</span>

              <button
                type="button"
                className={styles.sheetClose}
                aria-label="Close menu"
                onClick={() => setMoreOpen(false)}
              >
                <FiX />
              </button>
            </div>

            {user && (
              <div
                className={styles.profileCard}
                style={{ marginBottom: 14 }}
              >
                <div className={styles.profileAvatar}>
                  {user.full_name?.[0]?.toUpperCase() || '?'}
                </div>

                <div className={styles.profileInfo}>
                  <div className={styles.profileName}>
                    {user.full_name}
                  </div>
                  <div className={styles.profileRole}>
                    {user.role}
                  </div>
                </div>
              </div>
            )}

            <div className={styles.sheetGrid}>
              {MORE_ITEMS.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={styles.sheetItem}
                  aria-current={
                    location.pathname === item.path ? 'page' : undefined
                  }
                  onClick={() => setMoreOpen(false)}
                >
                  <item.Icon />
                  {item.label}
                </Link>
              ))}
            </div>

            <div
              className={styles.iconCol}
              style={{ marginTop: 14 }}
            >
              <Link
                to="/"
                className={styles.iconBtn}
                onClick={() => setMoreOpen(false)}
              >
                <FiExternalLink />
                <span>Website</span>
              </Link>

              <button
                type="button"
                className={styles.iconBtn}
                onClick={toggleTheme}
              >
                {theme === 'dark' ? <FiSun /> : <FiMoon />}
                <span>
                  {theme === 'dark' ? 'Light mode' : 'Dark mode'}
                </span>
              </button>

              <Link
                to="/dashboard/account"
                className={styles.iconBtn}
                onClick={() => setMoreOpen(false)}
              >
                <FiSettings />
                <span>Account</span>
              </Link>

              <button
                type="button"
                className={styles.iconBtn}
                onClick={handleLogout}
              >
                <FiLogOut />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardShell;